import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { dict, type DictKey, type Lang } from './dictionary';

export type { Lang, DictKey } from './dictionary';

const STORAGE_KEY = 'releasesnap_lang';

export function detectInitialLang(forced?: Lang): Lang {
  if (forced) return forced;
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (stored === 'ru' || stored === 'en') return stored;
  const browser = window.navigator.language ?? '';
  return browser.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

export function t(key: DictKey, lang: Lang): string {
  return dict[lang][key] ?? dict.en[key] ?? key;
}

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: DictKey) => string;
  /** When true, the language is fixed by the host (e.g. admin app) — switcher should hide. */
  fixed: boolean;
}

const LangContext = createContext<LangContextValue | null>(null);

interface LangProviderProps {
  /** Force a specific language (no switcher). Used by the admin Mini App. */
  fixed?: Lang;
  children: React.ReactNode;
}

export const LangProvider: React.FC<LangProviderProps> = ({ fixed, children }) => {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang(fixed));

  useEffect(() => {
    if (fixed) setLangState(fixed);
  }, [fixed]);

  const setLang = useCallback(
    (l: Lang) => {
      if (fixed) return;
      window.localStorage.setItem(STORAGE_KEY, l);
      setLangState(l);
    },
    [fixed],
  );

  const value: LangContextValue = {
    lang,
    setLang,
    t: (key) => t(key, lang),
    fixed: !!fixed,
  };

  return React.createElement(LangContext.Provider, { value }, children);
};

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) {
    throw new Error('useLang must be used inside <LangProvider>');
  }
  return ctx;
}
